import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { KTX2Loader } from 'three/addons/loaders/KTX2Loader.js';
import { color, float, smoothstep, texture, uniform, vec3, vec4 } from 'three/tsl';
import * as THREE from 'three/webgpu';
import type { Creature } from './kilnback-placeholder.ts';

/**
 * Glow masks for the baked-in elemental parts. The Tripo textures paint magma seams and water
 * orbs into the base color; the mask lifts them into the emissive MRT channel so the selective
 * bloom picks them up. Tuned by eye in the look test.
 */
const GLOW: Record<
  string,
  (map: THREE.Texture, heat: THREE.UniformNode<'float', number>) => THREE.Node
> = {
  // Magma: bright yellow-orange (high green) with little blue. Red scales stay unlit.
  kilnback: (map, heat) => {
    const c = vec4(texture(map));
    const mask = smoothstep(0.42, 0.68, c.g).mul(float(1).sub(smoothstep(0.25, 0.45, c.b)));
    return c.rgb
      .mul(mask)
      .mul(vec3(3.2, 2.2, 1.2))
      .mul(float(heat));
  },
  // Water orbs and crest highlights: bright cyan.
  rillwing: (map, heat) => {
    const c = vec4(texture(map));
    const mask = smoothstep(0.62, 0.85, c.b).mul(smoothstep(0.55, 0.8, c.g));
    return c.rgb.mul(mask).mul(color('#9fe8ff')).mul(float(heat).mul(1.6));
  },
};

let loader: GLTFLoader | null = null;

function getLoader(renderer: THREE.WebGPURenderer): GLTFLoader {
  if (loader) return loader;
  const ktx2 = new KTX2Loader().setTranscoderPath('/basis/').detectSupport(renderer);
  loader = new GLTFLoader().setMeshoptDecoder(MeshoptDecoder).setKTX2Loader(ktx2);
  return loader;
}

export interface CreatureModel extends Creature {
  clips: string[];
  play(name: string, fade?: number): void;
  socket(name: string): THREE.Object3D | undefined;
  /** The animated glTF scene under `root`: procedural offsets (lean, roll, squash) go here. */
  body: THREE.Object3D;
  mixer: THREE.AnimationMixer;
  actions: Map<string, THREE.AnimationAction>;
  /** Glow intensity multiplier (1 = rest); gameplay drives it later (Surge, charge). */
  setHeat(v: number): void;
}

export async function loadCreature(
  renderer: THREE.WebGPURenderer,
  id: string,
): Promise<CreatureModel> {
  const gltf = await getLoader(renderer).loadAsync(`/content/creatures/${id}/${id}.glb`);
  const root = new THREE.Group();
  root.name = id;
  root.add(gltf.scene);

  const heat = uniform(1);
  gltf.scene.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.frustumCulled = false; // skinned bounds don't follow the animation
    const src = mesh.material as THREE.MeshStandardMaterial;
    const mat = new THREE.MeshStandardNodeMaterial();
    mat.map = src.map;
    mat.normalMap = src.normalMap;
    mat.roughnessMap = src.roughnessMap;
    mat.roughness = src.roughness;
    // Creatures are organic (ceramic, feather, hide); Tripo's metalness channel reads as dark chrome.
    mat.metalness = 0;
    mat.side = THREE.FrontSide;
    const glow = GLOW[id];
    if (glow && src.map) mat.emissiveNode = glow(src.map, heat);
    mesh.material = mat;
  });

  const mixer = new THREE.AnimationMixer(gltf.scene);
  const actions = new Map(gltf.animations.map((clip) => [clip.name, mixer.clipAction(clip)]));
  let current: THREE.AnimationAction | undefined;

  const play = (name: string, fade = 0.25) => {
    const next = actions.get(name);
    if (!next || next === current) return;
    next.reset().play();
    if (current) next.crossFadeFrom(current, fade, true);
    current = next;
  };
  play(actions.has('idle') ? 'idle' : (gltf.animations[0]?.name ?? ''));

  let heatBase = 1;
  return {
    root,
    clips: [...actions.keys()],
    play,
    socket: (name) => gltf.scene.getObjectByName(name),
    body: gltf.scene,
    mixer,
    actions,
    setHeat(v) {
      heatBase = v;
    },
    update(dt, elapsed) {
      mixer.update(dt);
      heat.value = heatBase * (0.85 + Math.sin(elapsed * 1.7) * 0.15);
    },
  };
}
